import React from 'react';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import CardOnlineSettings from '../../../CardOnlineSettings';
import MercadoPagoSettings from './MercadoPagoSettings';
import PixWaitPaymentSettings from './PixWaitPaymentSettings';

export default function PaymentTab({ bandeirasCartao, bandeirasVale, handleSavePayment, loading, paymentMethods, setPaymentMethods }) {
  return (
        <div className="glass-card">
          <h2 className="text-xl sm:text-2xl font-semibold text-white mb-6 sm:mb-8">Formas de Pagamento Aceitas</h2>
          <div className="space-y-4">
            {Object.entries({
              dinheiro: { label: 'Dinheiro', emoji: '💵' },
              cartao: { label: 'Cartão na Entrega (maquininha)', emoji: '💳' },
              card_online_enabled: { label: 'Cartão Online (Mercado Pago)', emoji: '💳', online: true },
              pix: { label: 'PIX (Manual)', emoji: '🔷' },
              pix_mp: { label: 'PIX Automático (Mercado Pago)', emoji: '🚀' },
              vale_refeicao: { label: 'Vale Refeição', emoji: '🍽️' },
              vale_alimentacao: { label: 'Vale Alimentação', emoji: '🛒' }
            }).map(([key, { label, emoji, online }]) => (
              <div 
                key={key} 
                className={`flex items-center justify-between p-5 sm:p-6 rounded-xl border-2 transition-all duration-200 ${
                  paymentMethods[key]
                    ? 'bg-white/5 border-white/10 hover:border-white/20'
                    : 'bg-gray-800/30 border-gray-800/50'
                }`}
                style={{ minHeight: '80px' }}
              >
                <Label htmlFor={`payment-${key}`} className="flex items-center space-x-4 flex-1 cursor-pointer">
                  <div className="w-12 h-12 sm:w-10 sm:h-10 flex items-center justify-center bg-white/10 rounded-xl text-2xl">
                    {emoji}
                  </div>
                  <div className="flex-1">
                    <span className={`font-semibold text-base sm:text-lg block ${
                      paymentMethods[key] ? 'text-white' : 'text-gray-500'
                    }`}>
                      {label}
                    </span>
                    <span className={`text-sm ${
                      paymentMethods[key] ? 'text-green-400' : 'text-gray-600'
                    }`}>
                      {paymentMethods[key] ? 'Ativo' : 'Inativo'}
                      {online && paymentMethods[key] && ' • Configure parcelas em "Cartão Online"'}
                    </span>
                  </div>
                </Label>
                <div style={{ minWidth: '48px', minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Switch
                    id={`payment-${key}`}
                    checked={paymentMethods[key]}
                    onCheckedChange={(checked) => setPaymentMethods({ ...paymentMethods, [key]: checked })}
                    data-testid={`payment-${key}-toggle`}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Campo para Chave PIX - aparece quando PIX manual está ativo */}
          {paymentMethods.pix && (
            <div className="mt-6 p-5 bg-purple-500/10 border border-purple-500/30 rounded-xl">
              <div className="flex items-start space-x-3 mb-4">
                <div className="text-purple-400 text-xl mt-0.5">🔑</div>
                <div className="flex-1">
                  <p className="text-purple-400 text-sm font-medium mb-1">Chave PIX</p>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Cadastre sua chave PIX para que os clientes possam copiar e fazer o pagamento.
                  </p>
                </div>
              </div>
              <input
                type="text"
                value={paymentMethods.pix_key || ''}
                onChange={(e) => setPaymentMethods({ ...paymentMethods, pix_key: e.target.value })}
                placeholder="CPF, CNPJ, E-mail, Telefone ou Chave Aleatória"
                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                data-testid="pix-key-input"
              />
              <p className="text-gray-500 text-xs mt-2">
                Exemplo: 12345678901 (CPF) | email@exemplo.com | (11) 99999-9999
              </p>
            </div>
          )}

          {/* 💳 Configurações de Cartão Online - aparece quando ativo */}
          {paymentMethods.card_online_enabled && (
            <div className="mt-6 p-5 bg-blue-500/5 border border-blue-500/20 rounded-xl">
              <CardOnlineSettings token={localStorage.getItem('token')} embedded={true} />
            </div>
          )}

          {/* Bandeiras de Cartão - aparece quando Cartão está ativo */}
          {paymentMethods.cartao && (
            <div className="mt-6 p-5 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <div className="flex items-start space-x-3 mb-4">
                <div className="text-blue-400 text-xl mt-0.5">💳</div>
                <div className="flex-1">
                  <p className="text-blue-400 text-sm font-medium mb-1">Bandeiras de Cartão Aceitas</p>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Selecione as bandeiras de cartão que você aceita (opcional)
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {bandeirasCartao.map((bandeira) => (
                  <button
                    key={bandeira.id}
                    type="button"
                    onClick={() => {
                      const current = paymentMethods.cartao_bandeiras || [];
                      const updated = current.includes(bandeira.id)
                        ? current.filter(b => b !== bandeira.id)
                        : [...current, bandeira.id];
                      setPaymentMethods({ ...paymentMethods, cartao_bandeiras: updated });
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      (paymentMethods.cartao_bandeiras || []).includes(bandeira.id)
                        ? 'bg-blue-500 text-white'
                        : 'bg-white/10 text-gray-400 hover:bg-white/20'
                    }`}
                  >
                    {bandeira.nome}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Bandeiras de Vale Refeição - aparece quando Vale Refeição está ativo */}
          {paymentMethods.vale_refeicao && (
            <div className="mt-6 p-5 bg-orange-500/10 border border-orange-500/30 rounded-xl">
              <div className="flex items-start space-x-3 mb-4">
                <div className="text-orange-400 text-xl mt-0.5">🍽️</div>
                <div className="flex-1">
                  <p className="text-orange-400 text-sm font-medium mb-1">Bandeiras de Vale Refeição Aceitas</p>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Selecione as bandeiras de vale refeição que você aceita (opcional)
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {bandeirasVale.map((bandeira) => (
                  <button
                    key={bandeira.id}
                    type="button"
                    onClick={() => {
                      const current = paymentMethods.vale_refeicao_bandeiras || [];
                      const updated = current.includes(bandeira.id)
                        ? current.filter(b => b !== bandeira.id)
                        : [...current, bandeira.id];
                      setPaymentMethods({ ...paymentMethods, vale_refeicao_bandeiras: updated });
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      (paymentMethods.vale_refeicao_bandeiras || []).includes(bandeira.id)
                        ? 'bg-orange-500 text-white'
                        : 'bg-white/10 text-gray-400 hover:bg-white/20'
                    }`}
                  >
                    {bandeira.nome}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Bandeiras de Vale Alimentação - aparece quando Vale Alimentação está ativo */}
          {paymentMethods.vale_alimentacao && (
            <div className="mt-6 p-5 bg-green-500/10 border border-green-500/30 rounded-xl">
              <div className="flex items-start space-x-3 mb-4">
                <div className="text-green-400 text-xl mt-0.5">🛒</div>
                <div className="flex-1">
                  <p className="text-green-400 text-sm font-medium mb-1">Bandeiras de Vale Alimentação Aceitas</p>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Selecione as bandeiras de vale alimentação que você aceita (opcional)
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {bandeirasVale.map((bandeira) => (
                  <button
                    key={bandeira.id}
                    type="button"
                    onClick={() => {
                      const current = paymentMethods.vale_alimentacao_bandeiras || [];
                      const updated = current.includes(bandeira.id)
                        ? current.filter(b => b !== bandeira.id)
                        : [...current, bandeira.id];
                      setPaymentMethods({ ...paymentMethods, vale_alimentacao_bandeiras: updated });
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      (paymentMethods.vale_alimentacao_bandeiras || []).includes(bandeira.id)
                        ? 'bg-green-500 text-white'
                        : 'bg-white/10 text-gray-400 hover:bg-white/20'
                    }`}
                  >
                    {bandeira.nome}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 p-5 bg-blue-500/10 border border-blue-500/30 rounded-xl">
            <div className="flex items-start space-x-3">
              <div className="text-blue-400 text-xl mt-0.5">ℹ️</div>
              <div className="flex-1">
                <p className="text-blue-400 text-sm font-medium mb-1">Dica Importante</p>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Os clientes verão apenas as formas de pagamento ativas durante o checkout. Certifique-se de ativar pelo menos uma forma de pagamento.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-8 pt-6 border-t border-white/10">
            <button
              onClick={handleSavePayment}
              disabled={loading}
              className="glass-button-primary w-full sm:w-auto"
              style={{ minHeight: '48px', padding: '12px 32px', fontSize: '16px' }}
              data-testid="payment-save-button"
            >
              {loading ? 'Salvando...' : 'Salvar Formas de Pagamento'}
            </button>
          </div>

          {/* Mercado Pago Configuration Section */}
          <div className="mt-8 pt-8 border-t border-white/10">
            <h3 className="text-xl font-semibold text-white mb-4">Configuração Mercado Pago</h3>
            <p className="text-gray-400 text-sm mb-6">
              Configure seu Access Token do Mercado Pago para receber pagamentos PIX diretamente na sua conta
            </p>
            
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5 mb-6">
              <div className="flex items-start space-x-3">
                <div className="text-blue-400 text-xl mt-0.5">ℹ️</div>
                <div className="flex-1 text-sm text-gray-300">
                  <p className="font-semibold text-blue-400 mb-2">Como obter seu Access Token:</p>
                  <ol className="space-y-1 ml-4 list-decimal">
                    <li>Acesse <a href="https://www.mercadopago.com.br/developers" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Mercado Pago Developers</a></li>
                    <li>Entre na sua conta</li>
                    <li>Vá em &quot;Suas integrações&quot; &gt; &quot;Credenciais&quot;</li>
                    <li>Copie seu &quot;Access Token&quot; de produção</li>
                    <li>Cole abaixo para começar a receber pagamentos</li>
                  </ol>
                </div>
              </div>
            </div>

            <MercadoPagoSettings />
            
            {/* PIX Wait Payment Toggle */}
            <PixWaitPaymentSettings />
          </div>
        </div>
  );
}
